import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
	Space,
	ConfigProvider,
	Form,
	Input,
	Card,
	Row,
	Col,
	Tag,
	Tooltip,
	Switch,
	Button,
	Select,
	DatePicker,
	InputNumber
} from 'antd'
import {
	WarningOutlined,
	InfoCircleOutlined,
	HistoryOutlined,
	EyeOutlined,
	SettingOutlined
} from '@ant-design/icons'
import { runInAction, toJS } from 'mobx'
import { IS_NEW, SERVER_DATE_FORMAT, DATE_FORMAT } from '../../../config'
import { useStores } from '../../../cls/stores/MobXProvider'
import { SubmitButton } from '../../../components/Form'
import {
	BetweenDates,
	FormatedTimestamp,
	UserString
} from '../../../components/Shared'
import { getTourArrayByKeys } from '../../../stores/tour/config'
import '../../../design/styles/patterns.css' // Create a CSS file to handle custom styles
import {
	GeneralContentWrapper,
	GeneralTourTitle
} from '../../../components/Shared/GeneralComponents'
import {
	actSaveBudget,
	requireBudgetById
} from '../../../stores/budget/actions'
import Title from 'antd/es/typography/Title'
import { actLoadUsers } from '../../../stores/ui-lst-users/actions'
import dayjs from 'dayjs'
import { defaultCurrencyOptions } from '../../../components/Shared/Filter'
import AllowedCategories from '../../../components/Shared/AllowedCategories'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const { RangePicker } = DatePicker

/**
 * @function BudgetEdit
 * @description Component used for editing or viewing a budget item.
 * It handles form input, data population, saving updates, and rendering a tour-friendly UI.
 *
 * Props:
 * - `viewOnly` (boolean): if true, disables form interactions.
 *
 * Uses:
 * - MobX stores (`budget`, `users`, `tourStore`, `uiLstUsers`)
 * - Supports guided tours with IDs and tooltip markers
 * - Shows "marked for deletion" tag if applicable
 * - Supports interval sync toggle
 *
 * @returns {JSX.Element}
 */
const BudgetEdit = ({ viewOnly = false }) => {
	const navigate = useNavigate()
	const { itmId } = useParams()
	const { users, tourStore, uiLstUsers } = useStores()
	const { loggedInUserId } = users
	const { t } = useSmartTranslation()
	// Fetch the budget item based on the ID
	const _itm = requireBudgetById(itmId)

	const [form] = Form.useForm()

	// initial form values
	const _initialFormValues = {
		name: _itm.name,
		value: _itm.value,
		currency: _itm.currency,
		category_id: _itm.category_id,
		budget_type: _itm.budget_type === 'in',
		assigned_user_id: _itm.assigned_user_id || loggedInUserId,
		budgetPeriod: [
			_itm.period_start ? dayjs(_itm.period_start, 'YYYY-MM-DD') : null,
			_itm.period_end ? dayjs(_itm.period_end, 'YYYY-MM-DD') : null
		]
	}

	/**
	 * @function _doSave
	 * @description Saves the form data to the current budget store item,
	 * applies MobX updates, and triggers the API call to persist the item.
	 *
	 * @param {object} vals - Form values captured from the AntD Form.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			runInAction(() => {
				// Update the item properties based on form input
				_itm.setName(vals.name)
				_itm.setValue(vals.value)
				_itm.setCategory_id(vals.category_id)
				_itm.setBudgetType(vals.budget_type ? 'in' : 'out')
				_itm.setCurrency(vals.currency)
				_itm.setAssignedUserId(vals.assigned_user_id)
				_itm.setCreatedBy(_itm.created_by || loggedInUserId)
				// Setting the form values
				if (vals.budgetPeriod && vals.budgetPeriod.length === 2) {
					_itm.setPeriodStart(vals.budgetPeriod[0])
					_itm.setPeriodEnd(vals.budgetPeriod[1])
				}
			})
		}

		// console.log('_itm saving----', toJS(_itm))

		// Save the updated budget and navigate back to the list
		actSaveBudget(itmId, toJS(_itm.full))
			.then(() => {
				// navigate('/budget')
			})
			.catch(console.error)
	}

	/**
	 * @function useEffect (users load)
	 * @description Loads users list once the component mounts to support
	 * the "assigned user" dropdown selection.
	 */
	useEffect(() => {
		actLoadUsers().catch(console.error)
	}, [loggedInUserId])

	/**
	 * Start tour:
	 * This function starts the tour for the report form.
	 * It includes the general form, subtasks, comment and recurrence fields.
	 */
	const startTour = () => {
		tourStore.startTour('BudgetEdit-tour', [
			// General info
			...getTourArrayByKeys('info', ['name', 'modified']),

			// General form
			...getTourArrayByKeys('form', ['section'])
		])
	}

	const tourForm = getTourArrayByKeys('form', [
		'section',
		'name',
		'category_id',
		'for_user_id'
	])

	/**
	 * @function startTourForm
	 * @description Starts a form-focused tour, guiding users through
	 * the editable fields like name, value, category, and budget period.
	 */
	const startTourForm = () => {
		tourStore.startTour('SprintEdit-tour', [
			// General form
			...tourForm,
			{
				title: t('__single-value'),
				description: t('__single-balance%_budget'),
				target: () => document.getElementById('target-form-value')
			},
			{
				title: t('__field%single.currency'),
				description: t('single.currency'),
				target: () => document.getElementById('target-form-currency')
			},
			{
				title: t('__field%single.type'),
				description: t('_budget.type-in-out'),
				target: () => document.getElementById('target-form-type')
			},
			{
				title: t('__field%single.currency'),
				description: t('__period'),
				target: () => document.getElementById('target-form-period')
			}
		])
	}

	// Determine if the form should be disabled based on the viewOnly prop
	const [formIsDisabled] = React.useState(viewOnly)

	return (
		<GeneralContentWrapper>
			<GeneralTourTitle
				titleProps={{ text: t('_budget') }}
				buttonProps={{
					onClick: startTour
				}}
				rowProps={{
					justify: 'space-between'
				}}
			>
				{!!_itm.to_delete && (
					<Tooltip
						title={
							<>
								{t('single.delete-on')}
								<FormatedTimestamp timestamp={_itm.to_delete} />
							</>
						}
					>
						<Tag color="red" icon={<WarningOutlined />}>
							{t('single.delete-marked')}
						</Tag>
					</Tooltip>
				)}
			</GeneralTourTitle>

			<Card
				title={
					<span id="target-info-name">
						{viewOnly ? (
							<Tooltip title={t('__view%_budget')}>
								<EyeOutlined id="target-mode" /> {_itm.name}
							</Tooltip>
						) : _itm.loaded === IS_NEW ? (
							t('__create-new%_budget')
						) : (
							<Tooltip title={t('__edit%_budget')}>
								<SettingOutlined /> {_itm.name}
							</Tooltip>
						)}{' '}
					</span>
				}
				style={{ width: '100%' }}
				extra={
					<div id="target-info-modified">
						<Tooltip title={t('single.last-updated')}>
							<FormatedTimestamp
								timestamp={_itm.modifiedTs}
								formatedAs="DD.MM.YYYY hh:mm"
							/>{' '}
							<HistoryOutlined />
						</Tooltip>
					</div>
				}
			>
				<ConfigProvider>
					<Form
						layout="vertical"
						form={form}
						name="budget"
						initialValues={_initialFormValues}
						onFinish={_doSave}
						className={viewOnly && 'view-only-styles'}
					>
						<Row gutter={[50, 50]}>
							<Col span={24} className="section-pattern-dots">
								<Space
									style={{ width: '100%' }}
									direction="vertical"
									id="target-form"
								>
									<GeneralTourTitle
										align="end"
										titleProps={{
											text: 'General',
											h: Title,
											level: 5
										}}
										buttonProps={{
											onClick: startTourForm,
											disabled: false,
											style: { marginBottom: 4 }
										}}
									/>
									<Row gutter={[50, 50]}>
										<Col span={8} id="target-form-name">
											<Form.Item
												label={t('single.name')}
												tooltip={{
													title: t(
														'single.email-name-app-usage'
													),
													icon: <InfoCircleOutlined />
												}}
												name="name"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.name'
														)
													}
												]}
											>
												<Input
													style={{
														minWidth: '150px'
													}}
													disabled={formIsDisabled}
													showCount={!viewOnly}
													maxLength={100}
												/>
											</Form.Item>
										</Col>
										<Col span={8} id="target-form-value">
											<Form.Item
												label={t('single.value')}
												tooltip={{
													title: t(
														'__total%__value%_budget'
													),
													icon: <InfoCircleOutlined />
												}}
												name="value"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.value'
														)
													}
												]}
											>
												<InputNumber
													style={{
														minWidth: '200px'
													}}
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										<Col span={8} id="target-form-currency">
											<Form.Item
												label={t(
													'single.filter.currency'
												)}
												tooltip={{
													title: t(
														'__select.item%_currency'
													),
													icon: <InfoCircleOutlined />
												}}
												name="currency"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%single.currency'
														)
													}
												]}
											>
												<Select
													options={
														defaultCurrencyOptions
													}
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={[50, 50]}>
										<Col
											span={8}
											id="target-form-category_id"
										>
											<Form.Item
												label={t('single.category')}
												tooltip={{
													title: t('single.category'),
													icon: <InfoCircleOutlined />
												}}
												name="category_id"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%single.category'
														)
													}
												]}
											>
												<AllowedCategories
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										<Col
											span={8}
											id="target-form-for_user_id"
										>
											<Form.Item
												label={t('__assigned%_user')}
												tooltip={{
													title: t(
														'__select.item%_user'
													),
													icon: <InfoCircleOutlined />
												}}
												name="assigned_user_id"
											>
												<Select
													disabled={formIsDisabled}
													placeholder={t(
														'__select.item%_user'
													)}
													allowClear
													style={{
														minWidth: '200px'
													}}
													options={[
														{
															label: t(
																'__select.item%_user'
															),
															value: ''
														},
														...[
															...[loggedInUserId],
															...uiLstUsers.ids
														].map((uid) => {
															return {
																label: (
																	<UserString
																		key={
																			uid
																		}
																		userId={
																			uid
																		}
																		path={[
																			'name'
																		]}
																	/>
																),
																value: uid
															}
														})
													]}
												/>
											</Form.Item>
										</Col>
										<Col span={8} id="target-form-type">
											<Form.Item
												label={t('__type%_budget')}
												tooltip={{
													title: t('__type%_budget'),
													icon: <InfoCircleOutlined />
												}}
												name="budget_type"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%__period%_budget'
														)
													}
												]}
											>
												<Switch
													checkedChildren={t(
														'single.in'
													)}
													unCheckedChildren={t(
														'single.out'
													)}
													disabled={formIsDisabled}
												/>
											</Form.Item>
										</Col>
										<Col span={8} id="target-form-period">
											<Form.Item
												label={t('__period%_budget')}
												tooltip={{
													title: t(
														'__period%_budget'
													),
													icon: <InfoCircleOutlined />
												}}
												name="budgetPeriod"
											>
												{formIsDisabled ? (
													<BetweenDates
														dateFormat={
															SERVER_DATE_FORMAT
														}
														dates={[
															_itm.period_start,
															_itm.period_end
														]}
													/>
												) : (
													<RangePicker
														format={DATE_FORMAT}
														placeholder={[
															t(
																'single.start-date'
															),
															t('single.end-date')
														]}
														style={{
															width: '100%'
														}}
														disabled={
															formIsDisabled
														}
													/>
												)}
											</Form.Item>
										</Col>
									</Row>
								</Space>
							</Col>
						</Row>

						<Row gutter={[10, 10]} align="bottom">
							<Col>
								<SubmitButton>
									{_itm.loaded === IS_NEW
										? t('single.create')
										: t('single.update')}
								</SubmitButton>
							</Col>
							{
								// Show  cancel button only if editing resource
								!viewOnly && (
									<Col>
										<Button
											type="default"
											onClick={() => navigate(-1)}
										>
											{t('single.cancel')}
										</Button>
									</Col>
								)
							}
						</Row>
					</Form>
				</ConfigProvider>
			</Card>
		</GeneralContentWrapper>
	)
}
BudgetEdit.propTypes = {
	viewOnly: PropTypes.bool
}

export default observer(BudgetEdit)
